// ============================================================================
// DAS TICKET UTILITIES
// ============================================================================
// Utility functions for Digital Approval System ticket operations

import type { DASTicketListItem } from 'api/types';

/**
 * Helper to check if a stage represents a closed/final status
 */
const isClosedStage = (stage: string): boolean => {
  const closedStages = [
    'Approved',
    'Rejected',
    'Cancelled',
    // Two-Level Approval closed statuses
    'Closed - Approved via Consolidation',
    'Closed - Rejected via Consolidation',
  ];
  return closedStages.includes(stage);
};

/**
 * Helper function to filter tickets by tab
 */
export const filterTicketsByTab = (
  tickets: DASTicketListItem[],
  tabFilter: string
): DASTicketListItem[] => {
  switch (tabFilter) {
    case 'my-active-items':
      return tickets.filter(
        (t) =>
          t.yourActionStatus === 'Pending – Your action needed' ||
          // Show preview rows for the final approver too — these are pending
          // their action just not their turn yet (ShowWithRestriction mode).
          // The Take Action button is hidden for these rows via the
          // 'Pending – Your action needed' gate elsewhere.
          t.yourActionStatus === 'Pending – Awaiting previous approver' ||
          (t.yourRole === 'Creator' && !isClosedStage(t.currentStage))
      );
    case 'requests-created-by-me':
      return tickets.filter((t) => t.yourRole === 'Creator');
    case 'my-completed-actions':
      // Combined view: shows both in-progress and closed requests where user has taken action
      return tickets.filter(
        (t) =>
          t.yourActionStatus === 'Approved by You' ||
          t.yourActionStatus === 'Rejected by You' ||
          t.yourActionStatus === 'Recommended – Positive' ||
          t.yourActionStatus === 'Recommended – Adverse' ||
          t.yourActionStatus === 'Sent Back by You'
      );
    case 'pre-approved-items':
      // Pre-approved items are requests where the USER is the Pre-Approver (not Creator)
      // This shows items the user has pre-approved that are awaiting consolidation
      // Excludes consolidated requests created by the user (those show in my-active-items)
      return tickets.filter(
        (t) => t.currentStage === 'Pre Approved' && t.yourRole === 'Pre-Approver'
      );
    case 'all-tickets':
      return tickets;
    default:
      return tickets;
  }
};
